import React from "react";
import {
	Box,
	Container,
	Flex,
	Heading,
	IconButton,
	Img,
	Text,
	Link,
	UnorderedList,
	ListItem,
} from "@chakra-ui/react";
import { FaLinkedin } from "react-icons/fa";

const FundInFocusSep: React.FC = () => (
	<>
		{/* members */}
		<Flex
			as="section"
			flexDirection="column"
			overflow="hidden"
			justify="center"
			height={{ xs: "450px", sm: "300px" }}
			minH={{ xs: "450px", sm: "300px" }}
			maxH="450px"
			pos="relative"
		>
			<Img
				pos="absolute"
				objectFit="cover"
				src="/jpg/blogBanner.jpg"
				alt="members-hero"
				height="100%"
				width="100%"
			/>
			<Box
				bg="linear-gradient(93.48deg, #00001F 45.8%, #186658 74.6%)"
				pos="absolute"
				height="100%"
				width="100%"
				opacity="0.7"
			/>
			<Container
				maxW="container.xl"
				position="relative"
				zIndex="1"
				flexDir="column"
				px={{ xs: "20px", sm: "28px", md: "45px", lg: "60px", xl: "inherit" }}
			>
				<Heading
					as="h1"
					mb="20px"
					variant="reportCardTitle"
					fontSize={{ xs: "26px", sm: "28px", md: "32px", lg: "38px" }}
					color="white"
					lineHeight={{ xs: "34px", md: "39px", lg: "42px" }}
				>
					Raising the Bar: Quadria Capital&apos;s Evolution in Fundraising and
					Investor Relations
				</Heading>
			</Container>
		</Flex>
		<Container
			as="section"
			maxW="container.xl"
			pb="80px"
			pt="40px"
			minH="420px"
			display={{ xs: "flex", md: "inherit" }}
			flexDirection="column"
		>
			<Box as="main" w="100%" pb="40px">
				<Text variant="blogAnswer">
					Welcome to our ‘Fund in Focus’ series where we profile our member
					funds, underscore their investment philosophy, and highlight some of
					their interesting work. Today we speak to{" "}
					<Link
						href="https://www.linkedin.com/in/rahul-agarwal-b4068311?originalSubdomain=in"
						target="_blank"
						style={{
							textDecoration: "underline",
						}}
						rel="noreferrer"
					>
						Rahul Agarwal
					</Link>
					, Managing Director,{" "}
					<Link
						href="https://quadriacapital.com/"
						target="_blank"
						style={{
							textDecoration: "underline",
						}}
						rel="noreferrer"
					>
						Quadria Capital
					</Link>
					. In this feature, Rahul shares the investment thesis of the fund
					while also highlighting why India is a core market attracting maximum
					investment allocation. With strong focus on mid to late-stage stage
					companies, read on to know the fund’s investment strategy for the
					sub-segments in healthcare.
				</Text>
			</Box>

			<Img
				src="https://static.levocdn.com/WGLBUT82/7103258844226359534.jpg"
				alt="Rahul Agarwal"
				width="300px"
				float={{ xs: "right", md: "left" }}
				alignSelf={{ xs: "center", md: "flex-start" }}
				mx={{ xs: "0px", md: "0px" }}
				mr={{ xs: "0px", md: "40px", lg: "60px", xl: "70px" }}
				mb="20px"
				objectFit="contain"
			/>
			<Flex flexDir={{ xs: "column", lg: "row" }} rowGap="16px">
				<Heading
					textAlign={{ xs: "center", md: "left" }}
					fontWeight="800"
					color="blue.400"
				>
					Spokesperson: Rahul Agarwal, Managing Director, Quadria Capital
				</Heading>
				<Flex
					columnGap={{ xs: "4px", md: "16px" }}
					justifyContent={{ xs: "center", md: "flex-start" }}
					transform={{ xs: "translateX(12px)", md: "none" }}
				>
					<Box alignSelf="center">
						<Link
							href="https://www.linkedin.com/in/rahul-agarwal-b4068311?originalSubdomain=in"
							target="_blank"
						>
							<IconButton
								aria-label="linkedin"
								icon={
									<FaLinkedin size="28px" color="rgba(100, 100, 140, 0.6)" />
								}
								variant="unstyled"
								cursor="pointer"
							/>
						</Link>
					</Box>
				</Flex>
			</Flex>
			<Box>
				<Box
					ml={{ xs: "auto", md: "345px", lg: "370px" }}
					mr={{ xs: "auto", md: "0px" }}
					width={{ xs: "160px", md: "80px" }}
					h="2px"
					bgColor="primary.500"
					borderRadius="5px"
					mt={{ xs: "16px", md: "10px" }}
					mb="15px"
				/>
				<Box mt="16px" mb="20px">
					<Heading variant="blogQuestion" mb="20px">
						1. While describing Quadria Capital’s investment philosophy, do also
						share insight into the fund’s portfolio construction.
					</Heading>
					<Text variant="blogAnswer">
						Quadria Capital adopts a fundamentals and value driven investment
						approach, which is rooted in deep domain knowledge built over years
						of on-the-ground experience in the healthcare sector in Asia.
						Quadria’s investment strategy is focused on four key pillars: (a)
						Invest in What We Understand, (b) Invest by Adding Tangible Value,
						(c) Focused Investment Strike Zone and (d) Clear Pathway to Value
						Maximization. The Firm’s investment philosophy is to invest in
						healthcare businesses with stable and strong cash flows as well as
						competitively advantaged business models that exhibit substantial
						growth prospects and potential to emerge as regional leaders in the
						fast-growing Asian markets. In addition to providing strategic
						growth capital, Quadria works closely with its portfolio companies
						to drive tangible value creation, thus establishing a clear pathway
						towards shareholder value maximization at exit.
						<br />
						<br />
						Quadria seeks to create optimum risk return portfolio construct
						across all its funds. Portfolio construction is guided by (a)
						balanced allocation between South and Southeast Asia, (b)
						diversification across attractive healthcare sub-segments, and (c)
						diversification across different vintage years.
					</Text>
				</Box>
				{/* Question 2 */}
				<Box my="16px">
					<Heading variant="blogQuestion" pb="20px" pt="12px">
						2. Quadria Capital has successfully raised multiple funds. What has
						been the key to your fundraising success, and how has your investor
						base evolved over time?
					</Heading>

					<Text variant="blogAnswer" pt="8px">
						Over the years, Quadria has focused on building a strong
						relationship with its investors and gained their trust and
						confidence via (a) consistently deploying its stated investment
						strategy across multiple funds, (b) retaining and nurturing the best
						talent in Asian healthcare, (c) consistently delivering strong
						returns and distributions back to investors, (d) high quality and
						transparent reporting and (e) high co-investment allocation.
						<br />
						<br />
						Quadria is supported by some of the world’s marquee investors
						including leading asset managers/ fund of funds, DFIs and impact
						investors, pension and other institutional investors, family offices
						and healthcare strategics. Quadria has been successful in
						maintaining a high re-up rate with existing investors and adding new
						ones with each subsequent fund.
					</Text>
				</Box>

				{/* Question 3 */}
				<Box my="16px">
					<Heading variant="blogQuestion" pt="28px">
						3. What is the fund’s approach when investing into the Indian
						market? As a global fund do you see any lines of similarities with
						other regions?
					</Heading>
					<Text variant="blogAnswer" mt="16px" mb="8px">
						India is a core market for Quadria and receives the maximum country
						investment allocation from Quadria funds. We have a large office
						with on-the-ground investment and operating partners team. Quadria
						deploys the same investment style and strategy across all markets.
						The healthcare growth levers and challenges across India and
						Southeast Asian markets are strikingly similar. In fact, the stark
						similarity provides us an opportunity to get our portfolio companies
						from both the regions to collaborate and work with each other –
						something unique that only a regional fund like Quadria can offer.
						Quadria has helped stich multiple mutually beneficial business
						partnerships between its portfolio companies within India and
						Southeast Asia.
					</Text>
				</Box>

				<Box mt="8" mb="20px" gap="1rem">
					<Img
						mt="20px"
						ml="20px"
						mb="16px"
						src="https://static.levocdn.com/WGLBUT82/7103261412941389690.jpg"
						alt="Infographic"
						w="44%"
						float="right"
						objectFit="contain"
						display={{ xs: "none", sm: "block" }}
					/>
					<Box>
						{/* Question 4 */}
						<Box my="16px">
							<Heading variant="blogQuestion" pt="16px">
								4. Enterprises globally are working towards making
								sustainability an integral part of their DNA. What in your
								opinion are the benchmarks for measuring the impact generated by
								ESG focused enterprises? Any change in the attitude and mandate
								of LPs with respect to ESG compliance over the years?
							</Heading>
							<Text variant="blogAnswer" py="16px">
								Quadria Capital believes that responsible and impact-oriented
								investing is critical to long term, sustainable value creation
								and it is proud to invest in businesses that are proactively
								working to address the pressing challenges around healthcare
								provisioning and comply with best global ESG practices. The
								practices around measuring and reporting ESG and social impact
								performance are still evolving globally, and more so in our
								region. We have taken a step forward to develop a proprietary
								impact measurement and reporting framework, which helps assess
								investments based on their potential to generate positive change
								for the health system across four key impact pillars: Access,
								Affordability, Quality, and Awareness.
								<br />
								<br />
								In addition to potential financial returns, our investment
								committee evaluates each investment based on (a) its impact
								score at time of investment and proposed exit and (b) findings
								of ESG due-diligence and proposed corrective action plan. After
								the investment, our ESG and impact team works diligently with
								each portfolio company to achieve those improvements to ensure
								they score high on regulatory/ customer audits and buyer
								due-diligence at time of IPO/ exit process.
								<br />
								<br />
								Our ESG and impact framework and performance is very robust, and
								we score high on DFI and impact LP assessments, leading to very
								high re-up commitments from such LPs in all our funds.
							</Text>
						</Box>
						{/* Question 5 */}
						<Box my="16px">
							<Heading variant="blogQuestion" pt="16px">
								5. Quadria Capital has been known for its focus on healthcare
								and pharma investments. How does Quadria approach deal sourcing
								and due diligence to identify potential investment targets in
								the healthcare industry?
							</Heading>
							<Text variant="blogAnswer" py="16px">
								Quadria Capital&apos;s deep-rooted domain knowledge and
								entrenched relationships within the region and the sector enable
								it to access the highest quality, proprietary deal flow across
								its target healthcare subsectors. Quadria&apos;s substantial
								prior experience of value creation is a fundamental reason that
								entrepreneurs choose to partner with us. In building its
								proprietary pipeline, Quadria Capital uses a fundamental-driven
								approach by conducting a comprehensive analysis of the entire
								healthcare universe in our target region to identify potential
								leaders, and then build a long term relationship and mutual
								trust and confidence with these companies, even when there is no
								imminent deal. As a result, over 75% of deals completed by the
								Quadria Funds have been proprietary/ exclusive in nature.
							</Text>
						</Box>
						{/* Question 6 */}
						<Box my="16px">
							<Heading variant="blogQuestion" pt="16px">
								6. Apart from traditional healthcare related investments
								(Hospitals, API, Pharma), which are the other upcoming areas the
								fund anticipates growth in investment?
							</Heading>
							<Text variant="blogAnswer" py="16px">
								Quadria’s investment strategy is to primarily invest in mid to
								late growth stage companies, which are scaled up, profitable and
								have stable cash flows. We are seeing a strong trend of such
								companies emerging in many healthcare sub-segments, outside of
								the traditional segments. Some of these high growth sub-segments
								are medical device/ consumables, contract research/
								manufacturing businesses, consumer healthcare and technology
								driven healthcare models, amongst others. We evaluate
								opportunities in all attractive healthcare sub-segments and try
								to pick the best investments based on our internal criteria.
							</Text>
						</Box>
						{/* Question 7 */}
						<Box my="16px">
							<Heading variant="blogQuestion" pt="16px">
								7. The healthcare sector can be complex and heavily regulated.
								How does Quadria navigate the regulatory landscape while
								ensuring sustainable growth for its portfolio companies?
							</Heading>
							<Text variant="blogAnswer" py="16px">
								Quadria approaches regulatory risk management meticulously at
								various levels. We stay away from healthcare sub-segments which
								have existing or potential regulatory headwinds or risks which
								represent unsurmountable binary risks. For usual course
								management of regulatory compliances, we do a detailed legal and
								ESG due diligence before investing and make sure any identified
								deficiencies are corrected before our investment or there is a
								legally binding agreement to correct those after our investment.
								During the investment period, our deal and ESG teams monitor
								regulatory compliances via regular discussions and Ex-co/ Board
								level reporting for each portfolio company. We believe
								instilling a culture of discipline and risk monitoring helps
								tide over such risks effectively.
							</Text>
						</Box>
					</Box>
				</Box>

				{/* Question 8 */}
				<Box my="16px">
					<Heading variant="blogQuestion" pt="16px">
						8. Could you share some examples of successful portfolio companies
						that Quadria has supported and the value-add provided by the firm to
						help these companies thrive?
					</Heading>
					<Text variant="blogAnswer" py="16px">
						Quadria Capital’s in-depth understanding of healthcare, operational
						know-how, and proprietary healthcare ecosystem ensures a compelling
						hands-on value add proposition to our portfolio companies. We have
						been able to consistently demonstrate our value-add in our past and
						current investments. Couple of specific examples:
					</Text>
					<UnorderedList>
						<ListItem pb="14px">
							<Text variant="blogAnswer" pl={{ md: "12px", lg: "18px" }}>
								In one of our Indian hospital investments, the founder doctors
								were building a 3x capacity $100m capex hospital but had no
								experience or team to manage such a large greenfield project.
								Quadria seconded one of its team member to join the hospital
								team for two years, and led the project management, equipment
								procurement and hiring of key CXOs. Quadria’s intervention was
								critical in commissioning the project on time and the new
								hospital was EBITDA positive within a year,
							</Text>
						</ListItem>
						{/* 2nditem */}
						<ListItem pb="14px">
							<Text variant="blogAnswer" pl={{ md: "12px", lg: "18px" }}>
								In one of our Indian pharma investments, founders had run the
								business as a family run enterprise and now wanted to
								professionalize the team and operations. Quadria leveraged its
								network to (a) identify and hire 7 CXOs across business units
								and (b) put in place a system for monthly MIS review with
								individual business unit heads to develop key initiatives as
								well as strategic plans, especially on product portfolio
								selection and go-to-market strategy for key business units.
								Quadria also helped the company backward integrate by
								identifying and helping execute a synergistic acquisition.
							</Text>
						</ListItem>
					</UnorderedList>
				</Box>
				{/* Question 9 */}
				<Box my="16px">
					<Heading variant="blogQuestion" pt="16px">
						9. In today&apos;s competitive investment landscape, how does
						Quadria differentiate itself and maintain a strong track record of
						successful exits and returns for its investors?
					</Heading>
					<Text variant="blogAnswer" py="16px">
						Quadria’s key competitive strengths and differentiators are (a) one
						of Asia’s largest healthcare-focused investment and value creation
						team with deep domain knowledge and experience, that can help
						effectively source, evaluate and execute high quality transactions,
						(b) consistent application of fundamentals and value creation driven
						approach, which is rooted in deep domain knowledge built over years
						of on-the-ground experience in the healthcare sector and (c) deep
						track record of navigating the entire life cycle of private equity
						investments – from investment to value creation to exits. Preserving
						and utilizing these key strengths to make high quality investments,
						add value, create profitable exits and make consistent distributions
						to its LPs, have been the key reasons for Quadria’s growth and
						success in raising largest successive funds.
					</Text>
				</Box>
				{/* Question 10 */}
				<Box my="16px">
					<Heading variant="blogQuestion" pt="16px">
						10. Maintaining a transparent and collaborative relationship with
						LPs are crucial. What are some of the best practices which Quadria
						Capital follows throughout the investment lifecycle?
					</Heading>
					<Text variant="blogAnswer" py="16px">
						Since inception, Quadria has maintained a very strong engagement and
						relationship with its LPs. Quadria takes a very collaborative
						partnership approach by identifying each LP’s investment objective
						and unique requirements, and then diligently attempts to fulfil
						those requirements, be it co-investments, ESG/ impact compliances or
						other strategic needs. For example, till date Quadria has completed
						18 co-investment transactions with 13 LPs, which has helped LPs
						fulfil dual objectives of targeted capital deployment and
						accelerated returns. This collaborative partnership approach is the
						key success factor for Quadria raising successive larger funds with
						high re-up commitments from its LPs.
					</Text>
				</Box>
			</Box>
			<Heading
				variant="blogQuestion"
				lineHeight="30px"
				pt="16px"
				fontStyle="italic"
				p="12px 12px 0px"
			>
				The content in this section is curated by Team IVCA. For any feedback,
				connect with&nbsp;
				<Link
					href="mailto:paromita.sinha@ivca.in"
					target="_blank"
					style={{ color: "blue", textDecoration: "underline" }}
				>
					paromita.sinha@ivca.in
				</Link>
			</Heading>
		</Container>
	</>
);

export default FundInFocusSep;
